import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ColorInput,
  Divider,
  Group,
  Modal,
  NumberFormatter,
  NumberInput,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { taskOrIssueENUM } from "@mctypes/global.types";
import { ICapitalLetter, IOtherAttributeConfigItemProps } from "@schemas/otherAttributesSchema";
import { ColDef } from "ag-grid-community";
import { CustomCellEditorProps, CustomCellRendererProps } from "ag-grid-react";
import React, { forwardRef, Ref, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useImmer } from "use-immer";
import { useGetProjectByProjectIdIssueboardAndIssueBoardIdQuery } from "../../../../openapi";
import { STANDARD_COLORS } from "../../../app/globalConstants";
import { selectIsMathFormulaConfigValid } from "../../../app/routes/issuesApp/issueSelectors";
import { getIssueBoardIdFromUrl } from "../../../app/routes/issuesApp/issueUtils";
import { getProjectIdFromUrl } from "../../../app/utils";
import { useAppSelector } from "../../../hooks";
import { ConfirmAbortModal } from "../../ConfirmAbortModal/ConfirmAbortModal";
import ViewEditTable, { ViewEditTableRef } from "../../ViewEditTable/ViewEditTable";
import { VETEmptyAndNoDuplicatesValidator } from "../../ViewEditTable/ViewEditTableValidators";
import FormulaConfig from "./FormulaConfig";
import MathFormulaConfig from "./MathFormulaConfig/MathFormulaConfig";
import { IOtherAttributeConfigItemPropsWithId, TYPE_OF_VALUE_OPTIONS } from "./OtherAttributesConfig";

const OtherAttributeItemConfigModal: React.FC<{
  onClose: () => void;
  onSave: (otherAttributesConfigItemProps: IOtherAttributeConfigItemProps) => void;
  title: string;
  orignalOtherAttributesConfigItemProps?: IOtherAttributeConfigItemPropsWithId;
  addOrEdit: "add" | "edit";
  existingSections: string[];
  taskOrIssue?: taskOrIssueENUM;
  enableDropdownOptionDescriptions?: boolean;
}> = ({
  onClose,
  onSave,
  title,
  orignalOtherAttributesConfigItemProps = {} as IOtherAttributeConfigItemPropsWithId,
  addOrEdit,
  existingSections,
  taskOrIssue,
  enableDropdownOptionDescriptions = false,
}) => {
  const template: IOtherAttributeConfigItemProps = {
    typeOfValue: "STRING",
    label: "",
    labelColor: "",
    dropdownOptionType: "STRING",
    tableOptions: [],
    allowMultipleSelectionsInDropdown: false,
    defaultTextValue: undefined,
    defaultNumberValue: undefined,
    description: "",
    section: "",
    order: 0,
    isActive: true,
    formula: {
      nrOfCharsInCounter: 3,
      confArr: [],
    },
    mathFormula: {
      expression: "",
      variables: [],
    },
  };

  if (enableDropdownOptionDescriptions) {
    template.showDropdownOptionDescriptions = false;
  }

  const getScopedConfigItem = (configItem: IOtherAttributeConfigItemProps) => {
    if (enableDropdownOptionDescriptions) {
      return configItem;
    }

    const scopedConfigItem = { ...configItem };
    delete scopedConfigItem.showDropdownOptionDescriptions;
    return scopedConfigItem;
  };

  const initialConfigItem = getScopedConfigItem(
    addOrEdit === "add" ? template : orignalOtherAttributesConfigItemProps,
  );
  const editedOtherAttributesConfigItemPropsRef = useRef<IOtherAttributeConfigItemProps>(initialConfigItem);
  const [editedOtherAttributesConfigItemProps, setConfigItem] =
    useImmer<IOtherAttributeConfigItemProps>(initialConfigItem);

  const updateAttribute = (key: keyof IOtherAttributeConfigItemProps, value: any) => {
    editedOtherAttributesConfigItemPropsRef.current = {
      ...editedOtherAttributesConfigItemPropsRef.current,
      [key]: value,
    };
    setConfigItem((draft) => {
      (draft[key] as any) = value;
    });
  };

  const updateTypeOfValue = (value: string | null) => {
    const nextTypeOfValue = value as IOtherAttributeConfigItemProps["typeOfValue"];
    editedOtherAttributesConfigItemPropsRef.current = {
      ...editedOtherAttributesConfigItemPropsRef.current,
      typeOfValue: nextTypeOfValue,
      ...(value !== "DROPDOWN" ? { defaultDropdownValue: undefined } : {}),
      ...(value !== "STRING" && value !== "MULTILINESTRING" ? { defaultTextValue: undefined } : {}),
      ...(value !== "NUMBER" ? { defaultNumberValue: undefined } : {}),
    };
    setConfigItem((draft) => {
      draft.typeOfValue = nextTypeOfValue;
      if (value !== "DROPDOWN") {
        draft.defaultDropdownValue = undefined;
      }
      if (value !== "STRING" && value !== "MULTILINESTRING") {
        draft.defaultTextValue = undefined;
      }
      if (value !== "NUMBER") {
        draft.defaultNumberValue = undefined;
      }
    });
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const isMathFormulaConfigValid = useAppSelector((state) =>
    selectIsMathFormulaConfigValid(state, orignalOtherAttributesConfigItemProps?.id),
  );

  const isDisabled =
    editedOtherAttributesConfigItemProps.label === "" ||
    editedOtherAttributesConfigItemProps.section === "" ||
    (editedOtherAttributesConfigItemProps.typeOfValue === "MATH_FORMULA" && isMathFormulaConfigValid === false);

  const existingSectionOptions = existingSections.map((section) => section);

  const tableOptionsRef = useRef<TableOptionsRef>(null);

  const _closeInternal = () => {
    const tableData = tableOptionsRef.current?.getData();
    const current = {
      ...getScopedConfigItem(editedOtherAttributesConfigItemPropsRef.current),
      tableOptions: tableData?.rows ?? orignalOtherAttributesConfigItemProps?.tableOptions ?? [],
    };

    const changed = JSON.stringify(getScopedConfigItem(orignalOtherAttributesConfigItemProps)) !== JSON.stringify(current);

    // if (changed === true) {
    //   setConfirmModalOpen(true); // firing all the time
    // } else {
    //   onClose();
    // }

    onClose();
  };

  const _saveInternal = () => {
    const tableData = tableOptionsRef.current?.getData();
    const currentEditedOtherAttributesConfigItemProps = editedOtherAttributesConfigItemPropsRef.current;
    let newEditedOtherAttributesConfigItemProps = currentEditedOtherAttributesConfigItemProps;

    // type is DROPDOWN
    if (tableData != null) {
      const { rows, validationErrors } = tableData;
      const normalizedDropdownRows = normalizeDropdownOptionRows({
        rows,
        dropdownOptionType: currentEditedOtherAttributesConfigItemProps.dropdownOptionType,
      });
      const allValidationErrors = [...validationErrors, ...normalizedDropdownRows.validationErrors];

      if (allValidationErrors.length > 0) {
        setValidationErrors(allValidationErrors);
        setValidationModalOpen(true);
        return;
      }

      setConfigItem((draft) => {
        draft.tableOptions = normalizedDropdownRows.normalizedRows;
      });

      newEditedOtherAttributesConfigItemProps = {
        ...currentEditedOtherAttributesConfigItemProps,
        dropdownOptionType: currentEditedOtherAttributesConfigItemProps.dropdownOptionType ?? "STRING",
        tableOptions: normalizedDropdownRows.normalizedRows,
        defaultDropdownValue: normalizeDefaultDropdownValue({
          value: currentEditedOtherAttributesConfigItemProps.defaultDropdownValue,
          rows: normalizedDropdownRows.normalizedRows,
          dropdownOptionType: currentEditedOtherAttributesConfigItemProps.dropdownOptionType,
          allowMultipleSelectionsInDropdown:
            currentEditedOtherAttributesConfigItemProps.allowMultipleSelectionsInDropdown,
        }),
      };
      editedOtherAttributesConfigItemPropsRef.current = newEditedOtherAttributesConfigItemProps;
    }

    if (!["STRING", "MULTILINESTRING"].includes(newEditedOtherAttributesConfigItemProps.typeOfValue)) {
      newEditedOtherAttributesConfigItemProps = {
        ...newEditedOtherAttributesConfigItemProps,
        defaultTextValue: undefined,
      };
    } else if (newEditedOtherAttributesConfigItemProps.defaultTextValue === "") {
      newEditedOtherAttributesConfigItemProps = {
        ...newEditedOtherAttributesConfigItemProps,
        defaultTextValue: undefined,
      };
    }

    if (newEditedOtherAttributesConfigItemProps.typeOfValue !== "NUMBER") {
      newEditedOtherAttributesConfigItemProps = {
        ...newEditedOtherAttributesConfigItemProps,
        defaultNumberValue: undefined,
      };
    }

    newEditedOtherAttributesConfigItemProps = getScopedConfigItem(newEditedOtherAttributesConfigItemProps);

    const hasChanged =
      JSON.stringify(getScopedConfigItem(orignalOtherAttributesConfigItemProps)) !==
      JSON.stringify(newEditedOtherAttributesConfigItemProps);

    if (hasChanged === true) {
      onSave(newEditedOtherAttributesConfigItemProps);
      return;
    }

    onClose();
  };

  return (
    <>
      <Modal
        opened
        onClose={_closeInternal}
        title={title}
        size="850px"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <Stack id="dropdownContainer" gap="md">
          <TextInput
            label="Tittel"
            value={editedOtherAttributesConfigItemProps.label}
            onChange={(e) => updateAttribute("label", e.target.value)}
            required
          />
          <Textarea
            label="Beskrivelse"
            value={editedOtherAttributesConfigItemProps.description}
            onChange={(e) => updateAttribute("description", e.target.value)}
            autosize
            minRows={3}
          />
          <Autocomplete
            label="Seksjon"
            value={editedOtherAttributesConfigItemProps.section}
            onChange={(value) => updateAttribute("section", value)}
            onBlur={(e) => updateAttribute("section", e.currentTarget.value ?? "")}
            placeholder="Angi seksjon"
            data={existingSectionOptions}
            clearable
            required
          />
          <Select
            label="Type verdi"
            disabled={addOrEdit === "edit"}
            value={editedOtherAttributesConfigItemProps.typeOfValue}
            onChange={updateTypeOfValue}
            data={
              taskOrIssue === "TASK"
                ? TYPE_OF_VALUE_OPTIONS.filter((opt) => opt.value !== "FORMULA" && opt.value !== "MATH_FORMULA")
                : TYPE_OF_VALUE_OPTIONS
            }
          />
          {editedOtherAttributesConfigItemProps.typeOfValue === "STRING" && (
            <TextInput
              label="Standardverdi"
              description="Verdien fylles inn automatisk på nye saker i registeret."
              value={editedOtherAttributesConfigItemProps.defaultTextValue ?? ""}
              onChange={(e) => updateAttribute("defaultTextValue", e.currentTarget.value)}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "MULTILINESTRING" && (
            <Textarea
              label="Standardverdi"
              description="Verdien fylles inn automatisk på nye saker i registeret."
              value={editedOtherAttributesConfigItemProps.defaultTextValue ?? ""}
              onChange={(e) => updateAttribute("defaultTextValue", e.currentTarget.value)}
              autosize
              minRows={3}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "NUMBER" && (
            <NumberInput
              label="Standardverdi"
              description="Verdien fylles inn automatisk på nye saker i registeret."
              value={editedOtherAttributesConfigItemProps.defaultNumberValue ?? ""}
              onChange={(value) =>
                updateAttribute(
                  "defaultNumberValue",
                  typeof value === "number" && Number.isFinite(value) ? value : undefined,
                )
              }
              thousandSeparator=" "
              decimalSeparator=","
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "DROPDOWN" && (
            <TableOptions
              addOrEdit={addOrEdit}
              editedOtherAttributesConfigItemProps={editedOtherAttributesConfigItemProps}
              updateAttribute={updateAttribute}
              enableDropdownOptionDescriptions={enableDropdownOptionDescriptions}
              ref={tableOptionsRef}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "FORMULA" && (
            <FormulaConfig
              formula={
                editedOtherAttributesConfigItemProps.formula || {
                  nrOfCharsInCounter: 3,
                  confArr: [],
                }
              }
              onFormulaConfUpdate={(formula) => updateAttribute("formula", formula)}
            />
          )}
          {editedOtherAttributesConfigItemProps.typeOfValue === "MATH_FORMULA" && (
            <>
              <Alert color="yellow" icon={<i className="fa fa-flask fa-fw" />}>
                <Text fw={700}>BETA</Text>
                <Text size="sm">
                  Matematiske formler er fortsatt under innføring. Enkelte begrensninger og hvordan formler vises i
                  appen kan bli justert.
                </Text>
              </Alert>
              <MathFormulaConfig
                initialMathFormula={
                  editedOtherAttributesConfigItemProps.mathFormula || {
                    expression: "",
                    variables: [],
                  }
                }
                currentAttributeId={orignalOtherAttributesConfigItemProps?.id}
                onMathFormulaUpdate={(mathFormula) => updateAttribute("mathFormula", mathFormula)}
              />
            </>
          )}
          <Group align="end" gap="xs">
            <ColorInput
              label="Farge på tittel"
              value={editedOtherAttributesConfigItemProps.labelColor ?? ""}
              onChange={(newColor) => updateAttribute("labelColor", newColor ?? "")}
              swatches={Object.values(STANDARD_COLORS)}
              style={{ flex: 1 }}
            />
            <Button variant="default" onClick={() => updateAttribute("labelColor", "")}>
              Nullstill
            </Button>
          </Group>
          <Switch
            label={
              <Group gap={4}>
                <Text>Låst</Text>
                <Tooltip label="Låste attributter kan ikke endres i tabellen">
                  <i className="fa fa-info-circle fa-fw icon-info" />
                </Tooltip>
              </Group>
            }
            checked={editedOtherAttributesConfigItemProps.locked}
            onChange={(event) => updateAttribute("locked", event.currentTarget.checked)}
          />
          <Switch
            label={
              <Group gap={4}>
                <Text>Aktiv</Text>
                <Tooltip label="Inaktive attributter vises ikke">
                  <i className="fa fa-info-circle fa-fw icon-info" />
                </Tooltip>
              </Group>
            }
            checked={editedOtherAttributesConfigItemProps.isActive}
            onChange={(event) => updateAttribute("isActive", event.currentTarget.checked)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={_closeInternal}>
              Lukk
            </Button>
            <Button disabled={isDisabled} color="green" onClick={() => _saveInternal()}>
              Lagre
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ConfirmAbortModal
        isOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          setConfirmModalOpen(false);
          onClose();
        }}
      />
      <Modal
        opened={validationModalOpen}
        onClose={() => setValidationModalOpen(false)}
        title="Feil i tabellen"
        centered
      >
        <Stack>
          <Box component="ul" mb={0}>
            {validationErrors.map((error, index) => (
              <li key={index}>
                <pre style={{ fontFamily: "unset" }}>{error}</pre>
              </li>
            ))}
          </Box>
          <Text>Vennligst rett opp i feilene før du lagrer.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setValidationModalOpen(false)}>
              Ok
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default OtherAttributeItemConfigModal;

const ColorMenuEditor: React.FC<CustomCellEditorProps> = ({ value, onValueChange, stopEditing }) => {
  return (
    <Stack gap={4} p={6} bg="white" style={{ minWidth: "96px", border: "1px solid #dee2e6", borderRadius: 4 }}>
      {Object.values(STANDARD_COLORS).map((color) => (
        <Box
          component="button"
          type="button"
          key={color}
          onClick={() => {
            onValueChange(color);
            stopEditing();
          }}
          style={{
            padding: ".3em .6em",
            border: 0,
            background: "transparent",
            cursor: "pointer",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "20px",
              borderRadius: "5px",
              backgroundColor: color,
            }}
          />
        </Box>
      ))}
      <Box
        component="button"
        type="button"
        key={"clear"}
        onClick={() => {
          onValueChange("");
          stopEditing();
        }}
        style={{
          padding: ".3em .6em",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "20px",
            borderRadius: "5px",
            color: "black",
            fontSize: "14px",
          }}
        >
          <Box component="span" mr="0.25rem" style={{ marginBottom: "3px" }}>
            NULLSTILL
          </Box>
          <i className="fa fa-refresh"></i>
        </div>
      </Box>
    </Stack>
  );
};

interface TableOptionsRef {
  getData: () => {
    rows: TableOptionRow[];
    validationErrors: string[];
  };
}

type TableOptionRow = {
  value: string | number;
  description: string;
  color?: string;
};

const NO_DEFAULT_DROPDOWN_VALUE = "__NO_DEFAULT_DROPDOWN_VALUE__";

interface TableOptionsProps {
  addOrEdit: "add" | "edit";
  updateAttribute: (key: keyof IOtherAttributeConfigItemProps, value: any) => void;
  editedOtherAttributesConfigItemProps: IOtherAttributeConfigItemProps;
  enableDropdownOptionDescriptions: boolean;
}

const TableOptionsRefComp = (
  { updateAttribute, editedOtherAttributesConfigItemProps, addOrEdit, enableDropdownOptionDescriptions },
  ref: Ref<TableOptionsRef>,
) => {
  const history = useHistory();
  const location = history.location;
  const projectId = getProjectIdFromUrl(location.pathname);
  const issueBoardId = getIssueBoardIdFromUrl(location.pathname);

  const { data: issueBoard } = useGetProjectByProjectIdIssueboardAndIssueBoardIdQuery({
    projectId: projectId,
    issueBoardId: issueBoardId,
  });

  const otherAttirbutesConfigItemPropsWithDropdown = Object.entries(
    (issueBoard?.otherAttributesConfig || {}) as Record<string, any>,
  )
    .filter(([_propKey, content]) => {
      return content.typeOfValue === "DROPDOWN";
    })
    .map(([propKey, content]) => propKey);

  const isNumberDropdown = editedOtherAttributesConfigItemProps.dropdownOptionType === "NUMBER";
  const [dropdownOptionRows, setDropdownOptionRows] = useState<TableOptionRow[]>(
    (editedOtherAttributesConfigItemProps.tableOptions as TableOptionRow[]) || [],
  );

  const colDefs = useMemo<ColDef<TableOptionRow>[]>(
    () => [
      {
        field: "value",
        headerName: "Navn",
        editable: true,
        valueParser: (params) =>
          isNumberDropdown ? parseDropdownNumber(params.newValue) ?? params.newValue : params.newValue,
        cellRenderer: ({ value }: CustomCellRendererProps) => {
          if (!isNumberDropdown) {
            return <>{String(value ?? "")}</>;
          }

          const numberValue = parseDropdownNumber(value);
          if (numberValue == null) {
            return <>{String(value ?? "")}</>;
          }

          return <NumberFormatter value={numberValue} thousandSeparator=" " decimalSeparator="," />;
        },
      },
      {
        field: "description",
        headerName: "Beskrivelse",
        editable: true,
        flex: 1,
      },
      {
        field: "color",
        headerName: "Farge",
        editable: true,
        width: 100,
        cellRenderer: ({ value }: CustomCellRendererProps) => {
          return (
            <div
              style={{
                minWidth: "60px",
                height: "20px",
                borderRadius: "5px",
                backgroundColor: value,
                outline: value ? "" : "1px solid #e1e1e1",
                width: "100%",
              }}
            />
          );
        },
        cellEditor: ColorMenuEditor,
        cellEditorPopup: true,
      },
    ],
    [isNumberDropdown],
  );

  const newItemTemplateObject: { value: string; description: string } = {
    value: "",
    description: "",
  };

  const onCopyValuesFromOtherAttribute = (propKey: ICapitalLetter) => {
    const otherAttribute = issueBoard?.otherAttributesConfig[propKey];
    if (otherAttribute) {
      updateAttribute("tableOptions", otherAttribute.tableOptions);
      setDropdownOptionRows((otherAttribute.tableOptions as TableOptionRow[]) || []);
    }
  };

  const vetRef = useRef<ViewEditTableRef<TableOptionRow>>(null);
  const defaultDropdownOptions = Array.from(
    new Map(
      dropdownOptionRows
        .filter((option) => option.value !== "")
        .map((option) => {
          const value = formatDropdownValueForSelect(option.value);
          return [value, { value, label: value }];
        }),
    ).values(),
  );

  useImperativeHandle(ref, () => ({
    getData: () => vetRef.current?.getData(),
  }));

  return (
    <Paper withBorder p="md" radius="sm">
      <Stack gap="sm">
        <Group gap={4}>
          <Text size="sm" fw={600}>
            Verditype for alternativer
          </Text>
          <Tooltip label="Velg om alle alternativene i denne listen skal lagres som tekst eller tall">
            <i className="fa fa-info-circle fa-fw icon-info" />
          </Tooltip>
        </Group>
        <SegmentedControl
          value={editedOtherAttributesConfigItemProps.dropdownOptionType ?? "STRING"}
          onChange={(value) => {
            updateAttribute("dropdownOptionType", value);
            updateAttribute("defaultDropdownValue", undefined);
          }}
          data={[
            { value: "STRING", label: "Tekst" },
            { value: "NUMBER", label: "Tall" },
          ]}
          readOnly={addOrEdit === "edit"}
        />
        {addOrEdit === "edit" && (
          <Text c="dimmed" size="sm">
            Verditypen vises for eksisterende dropdowns, men kan ikke endres etter at attributten er opprettet.
          </Text>
        )}
        <Divider />
        <Text fw={600}>Alternativ</Text>
        {otherAttirbutesConfigItemPropsWithDropdown?.length > 0 && (
          <Box maw={240}>
            <Select
              value={null}
              onChange={(item) => item && onCopyValuesFromOtherAttribute(item)}
              placeholder="Kopier verdier fra?"
              data={otherAttirbutesConfigItemPropsWithDropdown}
            />
          </Box>
        )}
        <ViewEditTable<TableOptionRow>
          columns={colDefs}
          data={(editedOtherAttributesConfigItemProps.tableOptions as TableOptionRow[]) || []}
          // data is featched manually via getData
          onSave={() => {}}
          onRowsChange={setDropdownOptionRows}
          newItemTemplateObject={newItemTemplateObject}
          canEdit={true}
          dropdownContainer={"dropdownContainer"}
          alwaysInEditMode={true}
          validators={[VETEmptyAndNoDuplicatesValidator<{ value: string; description: string }>("value")]}
          ref={vetRef}
          useIndexForDragOrder={true}
        />
        {enableDropdownOptionDescriptions && (
          <>
            <Divider />
            <Switch
              label={
                <Group gap={4}>
                  <Text>Vis beskrivelser i nedtrekkslisten</Text>
                  <Tooltip label="Når denne er aktiv vises alternativets beskrivelse under navnet i dropdownen.">
                    <i className="fa fa-info-circle fa-fw text-info" />
                  </Tooltip>
                </Group>
              }
              checked={editedOtherAttributesConfigItemProps.showDropdownOptionDescriptions === true}
              onChange={(event) => updateAttribute("showDropdownOptionDescriptions", event.currentTarget.checked)}
            />
          </>
        )}
        <Divider />
        <Group gap={4}>
          <Text size="sm" fw={600}>
            Ett eller flere valg?
          </Text>
          <Tooltip label="Angi om du vil kunne velge ett eller flere valg fra listen">
            <i className="fa fa-info-circle fa-fw icon-info" />
          </Tooltip>
        </Group>
        <SegmentedControl
          value={editedOtherAttributesConfigItemProps.allowMultipleSelectionsInDropdown ? "MULTIPLE" : "SINGLE"}
          onChange={(value) => {
            updateAttribute("allowMultipleSelectionsInDropdown", value === "MULTIPLE");
            if (value === "MULTIPLE") {
              updateAttribute("defaultDropdownValue", undefined);
            }
          }}
          data={[
            { value: "SINGLE", label: "Ett valg" },
            { value: "MULTIPLE", label: "Fler valg" },
          ]}
          disabled={addOrEdit === "edit"}
        />
        {editedOtherAttributesConfigItemProps.allowMultipleSelectionsInDropdown !== true && (
          <Select
            label="Standardverdi"
            placeholder="Ingen standardverdi"
            value={
              editedOtherAttributesConfigItemProps.defaultDropdownValue == null
                ? NO_DEFAULT_DROPDOWN_VALUE
                : formatDropdownValueForSelect(editedOtherAttributesConfigItemProps.defaultDropdownValue)
            }
            onChange={(value) =>
              updateAttribute(
                "defaultDropdownValue",
                value == null || value === NO_DEFAULT_DROPDOWN_VALUE
                  ? undefined
                  : parseDropdownSelectValue(value, isNumberDropdown),
              )
            }
            data={[{ value: NO_DEFAULT_DROPDOWN_VALUE, label: "Ingen standardverdi" }, ...defaultDropdownOptions]}
            disabled={defaultDropdownOptions.length === 0}
            rightSectionPointerEvents="none"
          />
        )}
      </Stack>
    </Paper>
  );
};

const TableOptions = forwardRef<TableOptionsRef, TableOptionsProps>(TableOptionsRefComp);

const parseDropdownNumber = (value: unknown): number | null => {
  const cleaned = String(value ?? "")
    .trim()
    .replace(/[\s\u00A0\u202F]/g, "")
    .replace(",", ".");

  if (cleaned === "") {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isNaN(parsed) || Number.isFinite(parsed) === false ? null : parsed;
};

const normalizeDropdownOptionRows = ({
  rows,
  dropdownOptionType,
}: {
  rows: { value: string | number; description: string; color?: string }[];
  dropdownOptionType: IOtherAttributeConfigItemProps["dropdownOptionType"];
}) => {
  const validationErrors: string[] = [];
  const normalizedRows: { value: string | number; description: string; color?: string }[] = [];
  const seenValues = new Set<string>();

  rows.forEach((row, index) => {
    const rowNr = index + 1;

    if ((dropdownOptionType ?? "STRING") === "NUMBER") {
      const parsed = parseDropdownNumber(row.value);

      if (parsed == null) {
        validationErrors.push(`Rad ${rowNr}: Verdien må være et gyldig tall.`);
        return;
      }

      const dedupeKey = String(parsed);
      if (seenValues.has(dedupeKey)) {
        validationErrors.push(`Rad ${rowNr}: Verdien må være unik.`);
        return;
      }

      seenValues.add(dedupeKey);
      normalizedRows.push({ ...row, value: parsed });
      return;
    }

    const normalizedValue = String(row.value ?? "");
    if (seenValues.has(normalizedValue)) {
      validationErrors.push(`Rad ${rowNr}: Verdien må være unik.`);
      return;
    }

    seenValues.add(normalizedValue);
    normalizedRows.push({ ...row, value: normalizedValue });
  });

  return { normalizedRows, validationErrors };
};

const formatDropdownValueForSelect = (value: string | number): string => String(value ?? "");

const parseDropdownSelectValue = (value: string, isNumberDropdown: boolean): string | number => {
  if (!isNumberDropdown) {
    return value;
  }

  return parseDropdownNumber(value) ?? value;
};

const normalizeDefaultDropdownValue = ({
  value,
  rows,
  dropdownOptionType,
  allowMultipleSelectionsInDropdown,
}: {
  value: IOtherAttributeConfigItemProps["defaultDropdownValue"];
  rows: TableOptionRow[];
  dropdownOptionType: IOtherAttributeConfigItemProps["dropdownOptionType"];
  allowMultipleSelectionsInDropdown: IOtherAttributeConfigItemProps["allowMultipleSelectionsInDropdown"];
}) => {
  if (value == null || allowMultipleSelectionsInDropdown === true) {
    return undefined;
  }

  const normalizedValue = dropdownOptionType === "NUMBER" ? parseDropdownNumber(value) : String(value);
  if (normalizedValue == null) {
    return undefined;
  }

  return rows.some((row) => row.value === normalizedValue) ? normalizedValue : undefined;
};
